import MainLayout from "@/components/layout/admin/MainLayout";
import config from "@/assets/js/config.custom";
import Select2Multiple from "v-select2-multiple-component";
import axios from "axios";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import moment from "moment";
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';

const currentYear = moment().format('Y');

export default {
  components: {
    MainLayout,
    flatPickr,
    Select2Multiple,
    Loading

  },
  data() {
    return {
      searchData: {
        from_year: currentYear,
        to_year: currentYear
      },
      yearOption: [],
      acdYears: [],
      rawTransactionsItem: {
        income: [],
        expense: [],
        asset: [],
        investment: []
      },
      tax: 0,
      activeTab: 'profit-loss',
      yearInputConfigs: {
        dateFormat: 'Y',
        allowInput: true,
        disableMobile: true,
        onReady(_, __, instance) {
          instance.currentYearElement.focus()
        }
      },
      isFilterOpen: false,
      loading: false,
      searchError: '',
    };
  },
  
  computed: {
    currency() {
      return this.$store.state.setting.currency || 'MMK';
    },
    // Validate: to_year must be >= from_year
    dateRangeValid() {
      if (!this.searchData.from_year || !this.searchData.to_year) {
        return true;
      }
      return Number(this.searchData.to_year) >= Number(this.searchData.from_year);
    },
    yearColumns() {
      const columns = this.acdYears.map(item => ({
        id: item.id,
        period_name: item.period_name
      }));
      const knownIds = new Set(columns.map(col => Number(col.id)));

      // Include academic years that have amounts but were missing from the
      // year-range column API (e.g. periods without campus grades).
      const allItems = [
        ...this.rawTransactionsItem.income,
        ...this.rawTransactionsItem.expense,
        ...this.rawTransactionsItem.asset,
        ...this.rawTransactionsItem.investment,
      ];
      allItems.forEach(item => {
        (item.acdYears || []).forEach(acd => {
          const id = Number(acd.id);
          if (!id || knownIds.has(id)) {
            return;
          }
          knownIds.add(id);
          columns.push({
            id,
            period_name: acd.name || (`Year ${id}`)
          });
        });
      });

      return columns;
    },

    transactionsItem() {
      const transformItems = (items) => {
        return items.map(item => {
          const values = {};
          item.acdYears.forEach(acd => {
            values[acd.id] = acd.total;
          });
          const total = item.acdYears.reduce((sum, acd) => sum + acd.total, 0);
          return {
            id: item.id,
            name: item.name,
            values,
            total
          };
        });
      };

      return {
        incomes: transformItems(this.rawTransactionsItem.income),
        expenses: transformItems(this.rawTransactionsItem.expense),
        assets: transformItems(this.rawTransactionsItem.asset),
        investments: transformItems(this.rawTransactionsItem.investment)
      };
    },

    incomeTotalsByYear() {
      return this.calculateTotalsByYear(this.transactionsItem.incomes);
    },

    expenseTotalsByYear() {
      return this.calculateTotalsByYear(this.transactionsItem.expenses);
    },

    assetTotalsByYear() {
      return this.calculateTotalsByYear(this.transactionsItem.assets);
    },

    investmentTotalsByYear() {
      return this.calculateTotalsByYear(this.transactionsItem.investments);
    },

    totalIncomeAmount() {
      return this.getSectionTotal(this.transactionsItem.incomes);
    },

    totalExpenseAmount() {
      return this.getSectionTotal(this.transactionsItem.expenses);
    },

    totalAssetAmount() {
      return this.getSectionTotal(this.transactionsItem.assets);
    },

    totalInvestmentAmount() {
      return this.getSectionTotal(this.transactionsItem.investments);
    },

    // Profit/Loss per year
    profitLossByYear() {
      const result = {};
      this.yearColumns.forEach(({ id }) => {
        const income = this.incomeTotalsByYear[id] || 0;
        const expense = this.expenseTotalsByYear[id] || 0;
        result[id] = income - expense;
      });
      return result;
    },

    totalProfitLossKpi() {
      const income = this.totalIncomeAmount || 0;
      const expense = this.totalExpenseAmount || 0;
      return income - expense;
    },

    netProfitLossKpi() {
      return this.totalProfitLossKpi != 0 ? this.totalProfitLossKpi < 0 ? 'expense-card' : 'income-card' : "";
    },

    columnSpan() {
      return this.yearColumns.length + 2;
    }
  },

  watch: {
    // Validate date range when searchData changes
    searchData: {
      handler() {
        this.validateDateRange();
      },
      deep: true
    }
  },

  methods: {
    validateDateRange() {
      if (!this.searchData.from_year || !this.searchData.to_year) {
        this.searchError = '';
        return;
      }
      if (Number(this.searchData.to_year) < Number(this.searchData.from_year)) {
        this.searchError = 'To Year must be greater than or equal to From Year';
      } else {
        this.searchError = '';
      }
    },

    search() {
      this.validateDateRange();
      if (this.searchError) return;
      this.getAllTransaction();
    },

    async getAcademicRange() {
      await axios.get("getAcademicRange")
        .then(res => {
          for (let i = res.data.from_year; i <= res.data.to_year; i ++) {
            this.yearOption.push({
              id: i,
              text: i
            });
          }
        })
        .catch(err => {
          console.error(err);
        });

        this.defaultAcademicStart();
    },

    defaultAcademicStart() {
      const years = this.yearOption.map(y => y.id);

      for (let i = config.FINANCIAL_SUMMARY.DEFAULT_PREVIOUS_YEAR; i >= 0; i --) {
        if (i == 0) {
          this.searchData.from_year = this.yearOption[0]?.id;
          break; 
        }
        
        if (years.includes(currentYear - i)) {
          this.searchData.from_year = currentYear - i;
          break;
        }
      }
    },

    async getAcdYears() {
      await axios.get("getAcademicYearsByYearRange", {
        params: this.searchData
      })
      .then(res => {
        this.acdYears = res.data;
      })
      .catch(err => {
        console.error(err);
      });
    },

    async getAllTransaction() {
      this.loading = true;

      await this.getAcdYears();

      await Promise.all(
        Object.keys(this.rawTransactionsItem).map(key => this.getTransByType(key))
      );

      this.$nextTick(() => {
        this.loading = false;
      });
    },

    async getTransByType(type) {
      await axios.get(`report/transaction-by-acdyear/${type}`, {
        params: this.searchData
      })
        .then(res => {
          this.rawTransactionsItem[type] = res.data;
        })
        .catch(error => {
          if (error.response.status == 401) {
            this.$store.commit('logout');
            window.location.replace('login');
          }
          console.error(error);
        });
    },

    reset() {
      this.searchData.to_year = currentYear
      this.searchError = '';
      this.defaultAcademicStart();
      this.getAllTransaction();
    },

    /**
     * Calculate totals by academic year for a section
     * @param {array} items - Transformed transaction items
     * @returns {object} - { yearId: total }
     */
    calculateTotalsByYear(items) {
      const totals = {};
      this.yearColumns.forEach(({ id }) => {
        totals[id] = items.reduce((sum, item) => sum + (item.values[id] || 0), 0);
      });
      return totals;
    },

    /**
     * Get grand total for a section
     * @param {array} items 
     * @returns {number}
     */
    getSectionTotal(items) {
      return items.reduce((sum, item) => sum + item.total, 0);
    },

    /**
     * Get value for a specific year, returns 0 if not present
     * @param {object} item - Transformed transaction item
     * @param {number} yearId 
     * @returns {number}
     */
    getValueByYear(item, yearId) {
      return item.values[yearId] || 0;
    },

    fixFormat(value, negative = false) {
      let result = negative && value != 0 ? -value : value;
      return Number(result).toLocaleString();
    },

    /**
     * Format KPI card amounts like Profit/Loss Report:
     * rounded whole number + campus currency (e.g. "36,001,663 MMK").
     */
    formatCardAmount(value, negative = false) {
      const num = Number(value) || 0;
      const result = negative && num != 0 ? -num : num;
      return Math.round(result).toLocaleString('en-US') + ' ' + this.currency;
    }
  },

  async mounted() {
    this.loading = true;
    await this.getAcademicRange();
    
    if (this.searchData.from_year && this.searchData.to_year) {
      await this.getAcdYears();
    }

    this.getAllTransaction();
  }
}
